import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Shield } from 'lucide-react';

export const JuvelierizstradajumiRiga = () => {
  return (
    <>
      <Helmet>
        <title>Juvelierizstrādājumi Rīgā | Rotaslietas ar dimantiem | Pure Atelier</title>
        <meta name="description" content="Ekskluzīvi juvelierizstrādājumi Rīgā. Gredzeni, auskari ar dabīgiem dimantiem. Augstākā kvalitāte, labākās cenas. Pure Atelier - jūsu juvelieris." />
        <meta name="keywords" content="juvelierizstrādājumi Rīgā, rotaslietas ar dimantiem, juvelieris Rīgā, jewelry Riga" />
        <link rel="canonical" href="https://pureatelier.lv/juvelierizstradajumi-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1458867/pexels-photo-1458867.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Juvelierizstrādājumi Rīgā
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Ekskluzīvas rotaslietas ar dabīgiem dimantiem. Gredzeni un auskari, kas radīti ar meistarību un mīlestību.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Skatīt visu kolekciju <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20juvelierizstrādājumi." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Sazināties ar mums
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Mūsu kolekcija</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Link to="/gallery?category=rings" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-stone-100 p-8 hover:bg-stone-200 transition-colors">
                  <Diamond className="h-16 w-16 text-amber-600 mb-4" />
                  <h3 className="text-2xl font-serif text-stone-900 mb-2">Gredzeni</h3>
                  <p className="text-stone-600 mb-4">Saderināšanās gredzeni un gredzeni ar dimantiem</p>
                  <span className="text-amber-600 font-medium group-hover:underline">Skatīt gredzenu kolekciju →</span>
                </div>
              </Link>
              <Link to="/gallery?category=earrings" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-stone-100 p-8 hover:bg-stone-200 transition-colors">
                  <Sparkles className="h-16 w-16 text-amber-600 mb-4" />
                  <h3 className="text-2xl font-serif text-stone-900 mb-2">Auskari</h3>
                  <p className="text-stone-600 mb-4">Eleganti auskari ar dabīgiem dimantiem</p>
                  <span className="text-amber-600 font-medium group-hover:underline">Skatīt auskaru kolekciju →</span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Dabīgi dimanti", desc: "Sertificēti GIA/IGI" },
                { icon: Award, title: "Latvijā ražots", desc: "Vietējo meistaru darbs" },
                { icon: Sparkles, title: "Unikāls dizains", desc: "Individuāla pieeja" },
                { icon: Shield, title: "Garantija", desc: "Pilna izstrādājumu garantija" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Par Pure Atelier Riga
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> ir Rīgas juvelierizstrādājumu darbnīca, kas specializējas ekskluzīvās 
                  rotaslietās ar dabīgiem dimantiem. Mēs piedāvājam gan gatavus izstrādājumus, gan individuālu dizainu pēc pasūtījuma.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Mūsu vērtības</h3>
                <ul>
                  <li><strong>Kvalitāte</strong> — tikai augstākās kvalitātes materiāli un dimanti</li>
                  <li><strong>Caurspīdīgums</strong> — godīgas cenas bez slēptām piemaksām</li>
                  <li><strong>Individualitāte</strong> — katrs klients ir īpašs</li>
                  <li><strong>Meistarība</strong> — pieredzējuši Latvijas juvelieri</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Kāpēc Pure Atelier?</h3>
                <p>
                  Mēs esam vietējais Latvijas uzņēmums, kas strādā bez starpniekiem. Tas ļauj mums piedāvāt 
                  labākās cenas Rīgā, nezaudējot kvalitātē. Katrs mūsu izstrādājums ir meistardarbs, kas radīts ar rūpību.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Apmeklējiet mūsu kolekciju</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Sazināties ar mums WhatsApp, lai saņemtu bezmaksas konsultāciju par jebkuru juvelierizstrādājumu.
            </p>
            <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20juvelierizstrādājumi." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp konsultācija
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default JuvelierizstradajumiRiga;
